import {
  Fragment,
  useId,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
  type ReactNode,
} from 'react'
import { Check, ChevronDown, ChevronRight, Copy } from 'lucide-react'
import {
  editablePercent,
  PERCENT_DECIMALS,
  samePercent,
  validateWavePercent,
  WAVE_PERCENT_HINT,
} from './model'
import {
  aggregateDcRows,
  DC_TOLERANCE,
  dcCost,
  dcDelta,
  dcLabel,
  dcUnits,
  type DcRow,
  type DcVendorSection,
  vendorKey,
  vendorModelKey,
} from './dc-model'
import type { DerivedDC } from '@/api/distributionCenters'
import type { WavePlanDcVendorTotals } from '@/api/wavePlans'
import { Button } from '@/components/ui/button'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { toast } from '@/stores/useToastStore'
import { ColumnResizer } from './ColumnResizer'
import {
  useColumnSizing,
  type ColumnWidthsState,
  type SizedColumn,
} from './columnSizing'

/** A percent field that applies each valid value as it is typed (the split dialog). */
export function DcPercentInput({
  value,
  label,
  onChange,
}: {
  value: number
  label: string
  onChange: (value: number) => void
}) {
  const [editing, setEditing] = useState<string | null>(null)
  const id = useId()
  const error = editing !== null ? validateWavePercent(editing) : null
  return (
    <div className="dc-percent-field">
      <input
        aria-label={label}
        inputMode="decimal"
        value={editing ?? editablePercent(value)}
        aria-invalid={!!error}
        aria-describedby={error ? id : undefined}
        onFocus={event => {
          setEditing(editablePercent(value))
          event.target.select()
        }}
        onChange={event => {
          const next = event.target.value
          setEditing(next)
          if (!validateWavePercent(next)) onChange(Number(next.trim()))
        }}
        onBlur={() => setEditing(null)}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === 'Escape')
            event.currentTarget.blur()
        }}
      />
      <span aria-hidden="true">%</span>
      {error && (
        <div id={id} role="alert" className="dc-input-error">
          {error} Invalid value is not applied.
        </div>
      )}
    </div>
  )
}

/** A grid cell's percent: double-click, Enter, F2 or Space to edit; a changed value saves on commit. */
function DcPercentCell({
  value,
  label,
  readOnly = false,
  onChange,
}: {
  /** Null when the rows disagree ("Mixed"). */
  value: number | null
  label: string
  readOnly?: boolean
  onChange: (value: number) => void
}) {
  const [editing, setEditing] = useState<string | null>(null)
  const display = useRef<HTMLButtonElement>(null)
  const finished = useRef(false)
  const id = useId()
  const error =
    editing === null
      ? null
      : editing.trim()
        ? validateWavePercent(editing)
        : WAVE_PERCENT_HINT
  const shown = value === null ? 'Mixed' : `${dcUnits(value)}%`
  const start = () => {
    finished.current = false
    // At the stored precision: a split computed from quantities (33.333333%)
    // opens as 33.3333, which the unchanged-value check treats as the same.
    setEditing(value === null ? '' : editablePercent(value))
  }
  const finish = (cancel = false, restoreFocus = false) => {
    if (finished.current) return
    finished.current = true
    const next = editing === null ? null : Number(editing.trim())
    // Only a changed value is saved: moving through cells must not write.
    if (!cancel && error) toast.warning(`Not saved. ${error}`)
    if (
      !cancel &&
      next !== null &&
      !error &&
      (value === null || !samePercent(next, value))
    )
      onChange(next)
    setEditing(null)
    if (restoreFocus) requestAnimationFrame(() => display.current?.focus())
  }
  return (
    <div className="dc-percent-field">
      {editing === null ? (
        <button
          ref={display}
          type="button"
          className="dc-percent-value"
          aria-label={`${label}, ${shown}`}
          aria-keyshortcuts={readOnly ? undefined : 'Enter F2 Space'}
          title={
            readOnly ? undefined : 'Double-click to edit, or press Enter or F2'
          }
          disabled={readOnly}
          onDoubleClick={start}
          onKeyDown={event => {
            if (['Enter', 'F2', ' '].includes(event.key)) {
              event.preventDefault()
              start()
            }
          }}
        >
          {shown}
        </button>
      ) : (
        <>
          <input
            ref={element => {
              if (element && document.activeElement !== element) {
                element.focus()
                element.select()
              }
            }}
            aria-label={label}
            inputMode="decimal"
            value={editing}
            aria-invalid={!!error}
            aria-describedby={error ? id : undefined}
            onChange={event => setEditing(event.target.value)}
            onBlur={() => finish()}
            onKeyDown={event => {
              if (event.key === 'Escape') {
                event.preventDefault()
                finish(true, true)
              } else if (event.key === 'Enter') {
                event.preventDefault()
                if (!error) finish(false, true)
              }
            }}
          />
          <span aria-hidden="true">%</span>
          {error && (
            <div id={id} role="alert" className="dc-input-error">
              {error} Invalid value is not applied.
            </div>
          )}
        </>
      )}
    </div>
  )
}

/**
 * Browsers do not scroll a focused cell out from under sticky columns, header
 * or footer; an explicit scrollIntoView honours the scroller's scroll-padding.
 */
function revealFocused(event: FocusEvent<HTMLDivElement>) {
  if (event.target === event.currentTarget) return
  event.target.scrollIntoView({ block: 'nearest', inline: 'nearest' })
}

const NO_VIRTUAL_DCS: ReadonlySet<string> = new Set()

const NO_STORE_MODEL_NOTE =
  "No store model, or no store groups on the line: planned on the AP's Future Store Count and split by each DC's share of all stores."

/** Alerts that never change a quantity (Below MOQ / MOA): the SKU detail's POG badge. */
export const ALERT_BADGE =
  'inline-block whitespace-nowrap rounded bg-amber-100 px-1 text-[10px] font-medium text-amber-900'

/** A DC split percent with every stored decimal (an unbalanced split never reads 100%). */
const dcPercent = (value: number) =>
  value.toLocaleString('en-US', { maximumFractionDigits: PERCENT_DECIMALS })

/** The % these lines share at a DC (null: mixed). */
function commonPercent(rows: DcRow[], dcId: string): number | null {
  const first = rows[0]?.allocations[dcId].percent ?? null
  return rows.every(row => row.allocations[dcId].percent === first)
    ? first
    : null
}

/** Excess units read amber, a shortfall red. */
const balanceTone = (balance: number) =>
  balance > DC_TOLERANCE
    ? 'text-amber-800'
    : balance < -DC_TOLERANCE
      ? 'text-destructive'
      : 'text-muted-foreground'

/** A DC cell with nothing to edit: a routing placeholder or a DC not serving the lines. */
function InertDcCell({
  title,
  children = '—',
}: {
  title: string
  children?: ReactNode
}) {
  return (
    <td className="text-center text-[11px] text-muted-foreground" title={title}>
      {children}
    </td>
  )
}

/**
 * A vendor's or store model's row: its subtotals and PO cost over the lines
 * shown (filters apply) and, per DC, a percentage that applies to every line of
 * the group in the wave.
 */
function DcGroupRow({
  level,
  groupKey,
  name,
  detail,
  shown,
  lines,
  continued,
  centers,
  virtualDcIds,
  collapsed,
  readOnly,
  moa,
  onToggle,
  onEdit,
}: {
  level: 'vendor' | 'model'
  /** The vendor's or store model's key ('' = none), for the test id. */
  groupKey: string
  name: string
  /** What the group is ("Vendor · 3 store models"), before the line count. */
  detail: string
  /** The group's lines that match the filters (the subtotals). */
  shown: DcRow[]
  /** Every line of the group in the wave (what an edit changes). */
  lines: DcRow[]
  continued: boolean
  centers: DerivedDC[]
  virtualDcIds: ReadonlySet<string>
  collapsed: boolean
  readOnly: boolean
  /** A vendor's PO by DC in the wave, with its MOA check (vendor rows only). */
  moa?: WavePlanDcVendorTotals
  onToggle: () => void
  onEdit: (dcId: string, value: number) => void
}) {
  const totals = aggregateDcRows(shown, centers)
  const hidden = lines.length - shown.length
  return (
    <tr
      // An expanded group and everything shown in it share one colour; a
      // collapsed one is white.
      className={`dc-group-row ${level === 'vendor' ? 'dc-vendor-row' : 'dc-model-row'} ${collapsed ? 'dc-closed' : 'dc-open'}`}
      data-testid={`${level === 'vendor' ? 'dc-vendor' : 'dc-group'}-${groupKey || 'none'}`}
    >
      <th scope="rowgroup" className="dc-sku-column">
        <div className="flex items-start gap-1.5">
          <button
            type="button"
            className="dc-group-toggle"
            aria-expanded={!collapsed}
            aria-label={`${collapsed ? 'Expand' : 'Collapse'} ${name}`}
            onClick={onToggle}
          >
            {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
          </button>
          <div className="min-w-0">
            <span className="font-semibold">{name}</span>
            {continued && (
              <span className="ml-1 text-[10px] font-normal text-muted-foreground">
                (continued)
              </span>
            )}
            <span className="mt-1 block text-[10px] font-normal text-muted-foreground">
              {detail} · {lines.length} {lines.length === 1 ? 'line' : 'lines'}
              {hidden > 0 && ` · ${shown.length} shown`}
            </span>
          </div>
        </div>
      </th>
      <td className="dc-wave-column">
        <strong>{dcUnits(totals.waveQty)}</strong>
        <span className="mt-1 block text-[10px] text-muted-foreground">
          units
        </span>
      </td>
      {centers.map((dc, index) => {
        const served = lines.filter(row => row.allocations[dc.id])
        const subtotal = totals.centers[index]
        const cost = subtotal.cost ?? 0
        // The vendor's whole PO at the DC (every line, whatever the filters).
        const po = moa?.centers.find(center => center.dc_id === dc.id)
        const quantity = (
          <>
            <div
              className="dc-quantity"
              title={`${name} subtotal at ${dcLabel(dc)}: theoretical ${dcUnits(subtotal.raw)} units, adjusted ${dcUnits(subtotal.adjusted)} units, PO cost ${dcCost(cost)}.`}
            >
              <span>{dcUnits(subtotal.raw)}</span>
              <span aria-hidden="true">→</span>
              <strong>{dcUnits(subtotal.adjusted)}</strong>
            </div>
            {cost > 0 && <div className="dc-cost">{dcCost(cost)}</div>}
            {po && (po.below_moa_waves ?? 0) > 0 && moa?.moa != null && (
              <div className="mt-1">
                <span
                  className={ALERT_BADGE}
                  data-testid={`dc-below-moa-${dc.id}`}
                  title={`${name}'s PO at ${dcLabel(dc)} is ${dcCost(po.cost)}, below the vendor MOA of ${dcCost(moa.moa)}. Alert only: nothing is added.`}
                >
                  Below MOA
                </span>
              </div>
            )}
          </>
        )
        if (!served.length || virtualDcIds.has(dc.id))
          return (
            <InertDcCell
              key={dc.id}
              title={
                virtualDcIds.has(dc.id)
                  ? `${dc.name} is a routing placeholder and can't be edited`
                  : `${dcLabel(dc)} serves none of these lines`
              }
            >
              {served.length > 0 && (subtotal.adjusted > 0 || cost > 0)
                ? quantity
                : '—'}
            </InertDcCell>
          )
        return (
          <td key={dc.id}>
            <DcPercentCell
              value={commonPercent(served, dc.id)}
              label={`${name} ${dcLabel(dc)} allocation percent, all ${served.length} lines`}
              readOnly={readOnly}
              onChange={value => onEdit(dc.id, value)}
            />
            {quantity}
          </td>
        )
      })}
      <td className="dc-total-column">
        <strong className="block">
          {dcUnits(totals.adjusted)}{' '}
          <span className="text-[11px] font-normal text-muted-foreground">
            adjusted
          </span>
        </strong>
        <span className="dc-cost block">
          PO {dcCost(totals.cost ?? 0)}
          {(totals.costMissing ?? 0) > 0 && (
            <span
              className="ml-1 font-normal text-amber-800"
              title="Lines without a unit cost, or a Child whose PIAB contents are unknown, are not costed."
            >
              ({totals.costMissing} not costed)
            </span>
          )}
        </span>
        <span className={`dc-balance ${balanceTone(totals.balance)}`}>
          {dcDelta(totals.balance)} units vs. wave
        </span>
      </td>
    </tr>
  )
}

const ROUTING_UNKNOWN =
  "The AP has no case dimensions for this SKU, so it can't tell whether it's conveyable. By default it ships to all of its DCs until the case size is known."

/**
 * A Core line's routing: what the case is (conveyable or not) and where this
 * wave sends it (its DCs with a share), so the text never contradicts the
 * cells; a planner's split says so.
 */
function coreRouting(
  row: DcRow,
  centers: DerivedDC[],
  virtualDcIds: ReadonlySet<string>
): { text: string; unknown: boolean } | null {
  // Not sent (an older response): nothing to say.
  if (row.conveyable === undefined && !row.routingDefaulted) return null
  const unknown = !!row.routingDefaulted || row.conveyable == null
  const what = unknown
    ? 'Conveyable unknown'
    : row.conveyable === 'Yes'
      ? 'Conveyable'
      : 'Not conveyable'
  const shipped = centers.filter(
    dc => (row.allocations[dc.id]?.percent ?? 0) > DC_TOLERANCE
  )
  const [only] = shipped
  const where =
    shipped.length > 1
      ? `split across ${shipped.length} DCs`
      : !only
        ? null
        : virtualDcIds.has(only.id)
          ? only.name
          : `1 DC (${only.code})`
  return {
    text: [what, where, row.plannerSplit && 'planner split']
      .filter(Boolean)
      .join(' · '),
    unknown,
  }
}

/** One SKU line: its split per DC, rounding and PO cost. */
function DcLineRow({
  row,
  centers,
  virtualDcIds,
  core,
  readOnly,
  onEdit,
  onCopy,
}: {
  row: DcRow
  centers: DerivedDC[]
  virtualDcIds: ReadonlySet<string>
  core: boolean
  readOnly: boolean
  onEdit: (row: DcRow, dcId: string, value: number) => void
  onCopy: (row: DcRow) => void
}) {
  const routing = core ? coreRouting(row, centers, virtualDcIds) : null
  return (
    <tr className="dc-line-row dc-open" data-testid={`dc-row-${row.id}`}>
      <th scope="row" className="dc-sku-column">
        <div className="flex items-center justify-between gap-2">
          <span className="font-semibold">{row.line.sku}</span>
          <span className="dc-type">{row.line.type}</span>
        </div>
        <p
          className="mt-1 truncate text-[11px] font-normal"
          title={row.line.description}
        >
          {row.line.description}
        </p>
        <div className="mt-1 flex items-center justify-between gap-1">
          <span className="text-[11px] font-normal text-muted-foreground">
            Case pack: {dcUnits(row.logistics.multiple)} · MOQ{' '}
            {dcUnits(row.logistics.moq)}
            {row.belowMoq && (
              <>
                {' '}
                <span
                  className={ALERT_BADGE}
                  data-testid="dc-below-moq"
                  title={`Wave quantity ${dcUnits(row.waveQty)} is below the MOQ of ${dcUnits(row.logistics.moq)}. Alert only: no units are added.`}
                >
                  Below MOQ
                </span>
              </>
            )}
            {row.noStoreModel && (
              <>
                {' '}
                <span
                  className={ALERT_BADGE}
                  data-testid="dc-no-store-model"
                  title={NO_STORE_MODEL_NOTE}
                >
                  No store model
                </span>
              </>
            )}
            {row.unitCost != null &&
              ` · Unit cost ${row.unitCost.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`}
          </span>
          <Button
            variant="ghost"
            className="h-6 w-6 p-0"
            aria-label={`Copy ${row.line.sku} ${row.line.type} allocation to other waves`}
            title={
              row.otherWeekIds.length
                ? 'Copy this split to other waves for this SKU line'
                : 'This SKU line has no other waves'
            }
            disabled={readOnly || !row.otherWeekIds.length}
            onClick={() => onCopy(row)}
          >
            <Copy size={12} />
          </Button>
        </div>
        {routing && (
          <p
            data-testid="dc-routing"
            className={`mt-1 text-[11px] font-normal ${routing.unknown ? 'text-amber-800' : 'text-muted-foreground'}`}
            title={routing.unknown ? ROUTING_UNKNOWN : undefined}
          >
            {routing.text}
          </p>
        )}
        {row.sourceNeedsReview && (
          <p className="mt-1 text-[10px] font-normal text-amber-800">
            Review allocation in SKU detail
          </p>
        )}
        {row.line.type === 'Parent' && (
          <p className="mt-1 text-[10px] font-normal text-muted-foreground">
            Reference · excluded from unit totals
          </p>
        )}
      </th>
      <td className="dc-wave-column">
        <strong>{dcUnits(row.waveQty)}</strong>
        {row.moqDelta > 0 && (
          <span className="mt-1 block font-semibold text-amber-800">
            → {dcUnits(row.distributionQty)}
            <span className="block text-[10px] font-normal">MOQ</span>
          </span>
        )}
        <span className="mt-1 block text-[10px] text-muted-foreground">
          units
        </span>
      </td>
      {centers.map(dc => {
        const cell = row.allocations[dc.id]
        if (!cell)
          return (
            <InertDcCell
              key={dc.id}
              title={`${dcLabel(dc)} does not serve this SKU's stores`}
            />
          )
        return (
          <td key={dc.id}>
            <DcPercentCell
              value={cell.percent}
              label={`${row.line.sku} ${row.line.type} ${dcLabel(dc)} allocation percent`}
              readOnly={readOnly || virtualDcIds.has(dc.id)}
              onChange={value => onEdit(row, dc.id, value)}
            />
            <div
              className="dc-quantity"
              title={`${dcLabel(dc)}. Case pack size: ${dcUnits(row.logistics.multiple)} units. Theoretical: ${dcUnits(cell.raw)} units → rounded up to full case packs: ${dcUnits(cell.adjusted)} units. Difference: ${dcDelta(cell.delta)} units.`}
            >
              <span>{dcUnits(cell.raw)}</span>
              <span aria-hidden="true">→</span>
              <strong>{dcUnits(cell.adjusted)}</strong>
            </div>
          </td>
        )
      })}
      <td className="dc-total-column">
        <span
          className={`dc-status ${row.balanced ? 'dc-status-ok' : 'dc-status-review'}`}
        >
          {row.balanced && <Check size={11} />}
          {row.balanced ? '100' : dcPercent(row.percent)}% split
        </span>
        {!row.balanced && (
          <span className="mt-1 block text-[11px] text-destructive">
            {dcPercent(Math.abs(100 - row.percent))}%{' '}
            {row.percent < 100 ? 'remaining' : 'over target'}
          </span>
        )}
        <strong className="mt-1 block">
          {dcUnits(row.adjusted)}{' '}
          <span className="text-[11px] font-normal text-muted-foreground">
            adjusted
          </span>
        </strong>
        {row.poCost != null && (
          <span className="dc-cost block">PO {dcCost(row.poCost)}</span>
        )}
        <span className={`dc-balance ${balanceTone(row.balance)}`}>
          {row.balance > DC_TOLERANCE
            ? 'Excess '
            : row.balance < -DC_TOLERANCE
              ? 'Short '
              : ''}
          {dcDelta(row.balance)} units
          {Math.abs(row.balance) > DC_TOLERANCE && row.waveQty > 0 && (
            <> ({dcDelta((row.balance / row.waveQty) * 100)}%)</>
          )}
        </span>
      </td>
    </tr>
  )
}

export function DcAllocationGrid({
  sections,
  groupLines,
  totalRows,
  waveRows,
  centers,
  waveLabel,
  collapsed,
  onToggleGroup,
  onEdit,
  onEditAll,
  onEditGroup,
  onCopy,
  readOnly = false,
  virtualDcIds = NO_VIRTUAL_DCS,
  vendorTotals,
  core = false,
  columnWidths,
}: {
  /** This page's lines, by vendor and store model. */
  sections: DcVendorSection[]
  /** Every line of the wave by vendor / store model collapse key (what a group edit changes). */
  groupLines: Record<string, DcRow[]>
  totalRows: DcRow[]
  waveRows: DcRow[]
  centers: DerivedDC[]
  waveLabel: string
  collapsed: ReadonlySet<string>
  onToggleGroup: (key: string) => void
  onEdit: (row: DcRow, dcId: string, value: number) => void
  onEditAll: (dcId: string, value: number) => void
  /** A vendor's or store model's DC %: applies to every line of the group in the wave. */
  onEditGroup: (
    name: string,
    lines: DcRow[],
    dcId: string,
    value: number
  ) => void
  onCopy: (row: DcRow) => void
  readOnly?: boolean
  /** Routing placeholders ("Single DC Strategy"): shown read-only, never edited. */
  virtualDcIds?: ReadonlySet<string>
  /** The wave's PO by vendor and DC with the MOA check (by vendor key). */
  vendorTotals?: ReadonlyMap<string, WavePlanDcVendorTotals>
  /** A Core plan: each line shows its DC routing. */
  core?: boolean
  /** The user's column widths, kept by the panel across waves. */
  columnWidths?: ColumnWidthsState
}) {
  const totals = aggregateDcRows(totalRows, centers)
  const mobile = useMediaQuery('(max-width: 767px)')
  // A wider SKU column for the indented hierarchy, narrower DC columns; the DC
  // columns share any extra width.
  const columns = useMemo<SizedColumn[]>(
    () => [
      { key: 'sku', width: mobile ? 170 : 260, minWidth: 140 },
      { key: 'wave', width: 90, minWidth: 70 },
      ...centers.map(dc => ({ key: dc.id, width: 120, minWidth: 90 })),
      { key: 'total', width: mobile ? 160 : 190, minWidth: 140 },
    ],
    [centers, mobile]
  )
  const fill = useMemo(() => centers.map(dc => dc.id), [centers])
  const sizing = useColumnSizing(columns, fill, columnWidths)
  const resizer = (key: string, label: string, edge?: 'left') => (
    <ColumnResizer
      label={label}
      width={sizing.widths[key]}
      edge={edge}
      onResize={width => sizing.resize(key, width)}
      onReset={() => sizing.reset(key)}
    />
  )
  return (
    <div
      ref={sizing.containerRef}
      className="dc-grid-scroll"
      tabIndex={0}
      onFocus={revealFocused}
      role="region"
      aria-label="Distribution center allocation table, scroll for more destinations"
    >
      <table
        className="dc-allocation-table"
        style={{ width: sizing.tableWidth }}
      >
        <colgroup>
          {columns.map(column => (
            <col
              key={column.key}
              data-column={column.key}
              style={{ width: sizing.widths[column.key] }}
            />
          ))}
        </colgroup>
        <caption className="sr-only">
          {waveLabel}: lines grouped by vendor and store model. Double-click to
          edit allocation percentages for each SKU, store model or vendor, and
          distribution center. Each destination shows theoretical then adjusted
          units and the PO cost. Parent rows are excluded from unit totals.
        </caption>
        <thead>
          <tr>
            <th scope="col" className="dc-sku-column">
              SKU
              {resizer('sku', 'SKU')}
            </th>
            <th scope="col" className="dc-wave-column">
              Wave units<span>Planned</span>
              {resizer('wave', 'Wave units')}
            </th>
            {centers.map(dc => (
              <th
                scope="col"
                key={dc.id}
                title={
                  virtualDcIds.has(dc.id)
                    ? `${dc.name}: the designated DC is not known yet. Set a split on real DCs to replace it.`
                    : dcLabel(dc)
                }
              >
                {dc.code}
                {dc.name && dc.name !== dc.code && (
                  <span className="dc-center-name">{dc.name}</span>
                )}
                <span>
                  {virtualDcIds.has(dc.id)
                    ? 'Routing · read-only'
                    : 'Allocation %'}
                </span>
                {resizer(dc.id, dcLabel(dc))}
              </th>
            ))}
            <th scope="col" className="dc-total-column">
              Allocation check<span>Split / adjusted units</span>
              {resizer('total', 'Allocation check', 'left')}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr className="dc-all-skus-row" data-testid="dc-all-skus">
            <th scope="row" className="dc-sku-column">
              All SKUs
              <span className="mt-1 block text-[10px] font-normal text-muted-foreground">
                {waveRows.length} lines · entire selected wave
              </span>
            </th>
            <td className="dc-wave-column">—</td>
            {centers.map(dc => {
              // Only SKUs whose stores this DC serves take part.
              const served = waveRows.filter(row => row.allocations[dc.id])
              if (!served.length || virtualDcIds.has(dc.id))
                return (
                  <InertDcCell
                    key={dc.id}
                    title={
                      virtualDcIds.has(dc.id)
                        ? `${dc.name} is a routing placeholder and can't be edited`
                        : `${dcLabel(dc)} serves none of these SKUs`
                    }
                  />
                )
              return (
                <td key={dc.id}>
                  <DcPercentCell
                    value={commonPercent(served, dc.id)}
                    label={`All SKUs ${dcLabel(dc)} allocation percent`}
                    readOnly={readOnly}
                    onChange={value => onEditAll(dc.id, value)}
                  />
                </td>
              )
            })}
            <td className="dc-total-column text-[10px] text-muted-foreground">
              Applies across all filters and pages
            </td>
          </tr>
          {sections.map(section => {
            const vendor = section.vendor
            const vendorLines = groupLines[vendorKey(vendor.key)] ?? vendor.rows
            return (
              <Fragment key={`${vendor.key}|${section.continued}`}>
                <DcGroupRow
                  level="vendor"
                  groupKey={vendor.key}
                  name={vendor.name}
                  detail={`Vendor · ${vendor.models.length} ${vendor.models.length === 1 ? 'store model' : 'store models'}`}
                  shown={vendor.rows}
                  lines={vendorLines}
                  continued={section.continued}
                  centers={centers}
                  virtualDcIds={virtualDcIds}
                  collapsed={collapsed.has(vendorKey(vendor.key))}
                  readOnly={readOnly}
                  moa={vendorTotals?.get(vendor.key)}
                  onToggle={() => onToggleGroup(vendorKey(vendor.key))}
                  onEdit={(dcId, value) =>
                    onEditGroup(vendor.name, vendorLines, dcId, value)
                  }
                />
                {section.models.map(model => {
                  const key = vendorModelKey(vendor.key, model.group.key)
                  const modelLines = groupLines[key] ?? model.group.rows
                  return (
                    <Fragment key={`${key}|${model.continued}`}>
                      <DcGroupRow
                        level="model"
                        groupKey={model.group.key}
                        name={model.group.name}
                        detail="Store model"
                        shown={model.group.rows}
                        lines={modelLines}
                        continued={model.continued}
                        centers={centers}
                        virtualDcIds={virtualDcIds}
                        collapsed={collapsed.has(key)}
                        readOnly={readOnly}
                        onToggle={() => onToggleGroup(key)}
                        onEdit={(dcId, value) =>
                          onEditGroup(model.group.name, modelLines, dcId, value)
                        }
                      />
                      {model.rows.map(row => (
                        <DcLineRow
                          key={row.id}
                          row={row}
                          centers={centers}
                          virtualDcIds={virtualDcIds}
                          core={core}
                          readOnly={readOnly}
                          onEdit={onEdit}
                          onCopy={onCopy}
                        />
                      ))}
                    </Fragment>
                  )
                })}
              </Fragment>
            )
          })}
          {!sections.length && (
            <tr>
              <td
                colSpan={centers.length + 3}
                className="!py-14 text-center text-sm text-muted-foreground"
              >
                No SKU lines match these filters.
              </td>
            </tr>
          )}
        </tbody>
        <tfoot>
          <tr>
            <th scope="row" className="dc-sku-column">
              Filtered totals
              <span>{totalRows.length} lines · excludes Parent units</span>
            </th>
            <td>{dcUnits(totals.waveQty)}</td>
            {totals.centers.map(dc => (
              <td key={dc.id}>
                <span className="font-normal text-muted-foreground">
                  {dcUnits(dc.raw)}
                </span>
                <strong className="mt-1 block">{dcUnits(dc.adjusted)}</strong>
                <span className="dc-cost block">{dcCost(dc.cost ?? 0)}</span>
              </td>
            ))}
            <td className="dc-total-column">
              <strong>{dcUnits(totals.adjusted)}</strong>
              <span>{dcDelta(totals.balance)} vs. wave</span>
              <span className="dc-cost block">
                PO {dcCost(totals.cost ?? 0)}
              </span>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  )
}
